import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, Pencil, Save, Sparkles } from 'lucide-react';
import { useJournalDay } from '../../hooks/useJournalDay';
import { savePersonalNote, toDateKey } from '../../services/dailyLog';
import { updateSessionJournalNarrative } from '../../services/sessions';
import type { Session } from '../../types';
import MoodEnergyIndicator from '../mood/MoodEnergyIndicator';

interface JournalDayViewProps {
  userId: string | null;
  date: Date;
}

export function JournalDayView({ userId, date }: JournalDayViewProps) {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useJournalDay(userId, date);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [personalNote, setPersonalNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const isToday = toDateKey(date) === toDateKey(new Date());

  useEffect(() => {
    setPersonalNote(data?.dailyLog?.personalNote ?? '');
  }, [data?.dailyLog?.personalNote]);

  const sessionBuckets = useMemo(() => {
    const sessions = data?.sessions ?? [];
    const morning = sessions.find((session) => session.type === 'morning_checkin') ?? null;
    const evening = sessions.find((session) => session.type === 'evening_checkin') ?? null;
    const middle = sessions.filter(
      (session) => session.id !== morning?.id && session.id !== evening?.id
    );
    return { morning, middle, evening };
  }, [data?.sessions]);

  const synthesis =
    data?.dailyLog?.dailySynthesis
    || data?.sessions.find((session) => session.outputs?.journalNarrative)?.outputs?.journalNarrative
    || 'Your daily synthesis will appear here as more sessions are completed.';

  async function handleSaveNote() {
    if (!userId || !data) return;
    setSavingNote(true);
    setSaveFeedback(null);
    try {
      await savePersonalNote(userId, data.dateKey, personalNote);
      setSaveFeedback('Saved');
      refetch();
    } catch (err) {
      setSaveFeedback(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  }

  if (loading) {
    return (
      <div className="flank-card rounded-xl p-5 flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
        <Loader2 size={16} className="animate-spin" />
        Loading journal...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flank-card rounded-xl p-5">
        <p className="text-sm text-[var(--color-error)]">{error}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const hasSessions = data.sessions.length > 0;
  const morningMood = resolveMoodEnergyValue(
    sessionBuckets.morning?.stateSnapshot?.mood,
    data.dailyLog?.metrics?.morningMood
  );
  const morningEnergy = resolveMoodEnergyValue(
    sessionBuckets.morning?.stateSnapshot?.energy,
    data.dailyLog?.metrics?.morningEnergy
  );
  const eveningMood = resolveMoodEnergyValue(
    sessionBuckets.evening?.stateSnapshot?.mood,
    data.dailyLog?.metrics?.eveningMood
  );
  const eveningEnergy = resolveMoodEnergyValue(
    sessionBuckets.evening?.stateSnapshot?.energy,
    data.dailyLog?.metrics?.eveningEnergy
  );
  const hasMoodEnergy = Boolean(morningMood || morningEnergy || eveningMood || eveningEnergy);
  const morningIntention = firstNonEmpty(
    data.dailyLog?.morning?.intention,
    sessionBuckets.morning?.outputs?.dailyIntention
  );
  const morningMicroActions = sanitizeTextList(
    data.dailyLog?.morning?.microActions
      ?? sessionBuckets.morning?.outputs?.microActions
      ?? []
  );
  const eveningReflection = firstNonEmpty(
    data.dailyLog?.evening?.reflectionSummary,
    sessionBuckets.evening?.outputs?.reflectionSummary,
    sessionBuckets.evening?.outputs?.sessionSummary
  );
  const hasMorningHighlights = Boolean(morningIntention || morningMicroActions.length > 0);
  const hasEveningHighlights = Boolean(eveningReflection);
  const hasCheckInHighlights = hasMorningHighlights || hasEveningHighlights;
  const checkInHighlightCount = Number(hasMorningHighlights) + Number(hasEveningHighlights);

  return (
    <div className="flex flex-col gap-4">
      <section
        className="rounded-xl p-4 border"
        style={{
          borderColor: 'var(--color-border)',
          background: 'linear-gradient(160deg, color-mix(in srgb, var(--color-brand-primary) 12%, transparent), var(--color-surface) 60%)',
        }}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[var(--color-brand-primary)]" />
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Daily synthesis</h3>
        </div>
        <p className="text-sm text-[var(--color-text-secondary)] mt-2 leading-relaxed">{synthesis}</p>
      </section>

      {hasMoodEnergy && (
        <MoodEnergyIndicator
          morningMood={morningMood}
          morningEnergy={morningEnergy}
          eveningMood={eveningMood}
          eveningEnergy={eveningEnergy}
        />
      )}

      {hasCheckInHighlights && (
        <section className="flank-card rounded-xl p-4">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Check-in highlights</h3>
          <div className={`mt-3 grid gap-3 ${checkInHighlightCount > 1 ? 'md:grid-cols-2' : ''}`}>
            {hasMorningHighlights && (
              <div className="rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">Morning intention</p>
                {morningIntention && (
                  <p className="mt-2 text-sm text-[var(--color-text-primary)] leading-relaxed">{morningIntention}</p>
                )}
                {morningMicroActions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">Micro-actions</p>
                    <ul className="mt-2 flex flex-col gap-1.5 text-sm text-[var(--color-text-secondary)]">
                      {morningMicroActions.map((item) => (
                        <li key={item} className="leading-relaxed">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {hasEveningHighlights && (
              <div className="rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">Evening reflection</p>
                <p className="mt-2 text-sm text-[var(--color-text-primary)] leading-relaxed">{eveningReflection}</p>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="flank-card rounded-xl p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Session journal</h3>
        <div className="mt-3 flex flex-col gap-2">
          {sessionBuckets.morning && (
            <SessionNarrativeCard
              label="Morning"
              session={sessionBuckets.morning}
              userId={userId}
              expanded={expandedSessionId === sessionBuckets.morning.id}
              onToggle={() => setExpandedSessionId((prev) => prev === sessionBuckets.morning?.id ? null : sessionBuckets.morning?.id ?? null)}
              onSaved={refetch}
            />
          )}

          {sessionBuckets.middle.map((session) => (
            <SessionNarrativeCard
              key={session.id}
              label="During the day"
              session={session}
              userId={userId}
              expanded={expandedSessionId === session.id}
              onToggle={() => setExpandedSessionId((prev) => prev === session.id ? null : session.id)}
              onSaved={refetch}
            />
          ))}

          {sessionBuckets.evening && (
            <SessionNarrativeCard
              label="Evening"
              session={sessionBuckets.evening}
              userId={userId}
              expanded={expandedSessionId === sessionBuckets.evening.id}
              onToggle={() => setExpandedSessionId((prev) => prev === sessionBuckets.evening?.id ? null : sessionBuckets.evening?.id ?? null)}
              onSaved={refetch}
            />
          )}

          {!hasSessions && (
            <div className="rounded-lg border border-dashed border-[var(--color-border)] p-3">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">No sessions for this day yet</p>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                Start a check-in and this page will auto-build your daily journal narrative.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => navigate('/session?type=morning_checkin')}
                  className="flank-btn flank-btn-primary !h-8 !px-3"
                >
                  Morning check-in
                </button>
                <button
                  onClick={() => navigate('/session?type=evening_checkin')}
                  className="flank-btn flank-btn-secondary !h-8 !px-3"
                >
                  Evening reflection
                </button>
                {!isToday && (
                  <button
                    onClick={() => navigate(`/journal/${toDateKey(new Date())}`)}
                    className="flank-btn flank-btn-secondary !h-8 !px-3"
                  >
                    Back to today
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="flank-card rounded-xl p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">What Flank learned today</h3>
        <p className="text-xs text-[var(--color-text-secondary)] mt-2">
          {data.sessions.reduce((total, session) => total + (session.extraction?.nodesCreated?.length ?? 0), 0)} new beliefs, {' '}
          {data.sessions.reduce((total, session) => total + (session.extraction?.edgesCreated?.length ?? 0), 0)} new connections, {' '}
          {data.sessions.reduce((total, session) => total + (session.extraction?.motifsIdentified?.length ?? 0), 0)} patterns.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/insights?range=today&sort=mentions')}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-[var(--color-surface)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Today&apos;s beliefs
            <ChevronRight size={13} />
          </button>
          <button
            onClick={() => navigate('/insights/memories?range=today')}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-[var(--color-surface)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Today&apos;s memories
            <ChevronRight size={13} />
          </button>
        </div>
      </section>

      <section className="flank-card rounded-xl p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Personal note</h3>
        <textarea
          value={personalNote}
          onChange={(event) => setPersonalNote(event.target.value)}
          className="w-full mt-2 p-3 rounded-lg bg-[var(--color-surface)] text-sm text-[var(--color-text-primary)] border border-[var(--color-border)] min-h-[100px]"
          placeholder="Add your own note for this day..."
        />
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-[var(--color-text-muted)]">{toDateKey(date)}</p>
          <button
            onClick={handleSaveNote}
            disabled={!userId || savingNote}
            className="flank-btn flank-btn-secondary !h-8 !px-3"
          >
            {savingNote ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            <span className="ml-1">Save</span>
          </button>
        </div>
        {saveFeedback && (
          <p className="text-xs mt-1 text-[var(--color-text-secondary)]">{saveFeedback}</p>
        )}
      </section>
    </div>
  );
}

interface SessionNarrativeCardProps {
  label: string;
  session: Session;
  userId: string | null;
  expanded: boolean;
  onToggle: () => void;
  onSaved: () => void;
}

function SessionNarrativeCard({ label, session, userId, expanded, onToggle, onSaved }: SessionNarrativeCardProps) {
  const narrative = session.outputs?.journalNarrative || session.outputs?.sessionSummary || '';
  const preview = narrative.length > 180 ? `${narrative.slice(0, 180)}...` : narrative;
  const time = getSessionDate(session).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(narrative);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    setDraft(narrative);
  }, [narrative, session.id]);

  async function handleSaveNarrative() {
    if (!userId) return;
    setSaving(true);
    setFeedback(null);
    try {
      await updateSessionJournalNarrative(userId, session, draft.trim());
      setEditing(false);
      setFeedback('Saved');
      onSaved();
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-[var(--color-border)] overflow-hidden transition-colors hover:border-[color-mix(in_srgb,var(--color-brand-primary)_35%,var(--color-border))]">
      <button onClick={onToggle} className="w-full px-3 py-2 text-left flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[var(--color-text-muted)]">{label} · {time}</p>
          <p className="text-sm text-[var(--color-text-primary)] mt-1">
            {preview || 'No narrative available yet.'}
          </p>
        </div>
        <ChevronDown
          size={14}
          className="mt-1 text-[var(--color-text-muted)] transition-transform duration-200"
          style={{ transform: expanded ? 'rotate(180deg)' : undefined }}
        />
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-[var(--color-border)]">
          {!editing && (
            <p className="text-sm text-[var(--color-text-primary)] mt-2 whitespace-pre-wrap">
              {narrative || 'No narrative available yet.'}
            </p>
          )}
          {editing && (
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="w-full mt-2 p-2 rounded-lg bg-[var(--color-surface)] text-sm text-[var(--color-text-primary)] border border-[var(--color-border)] min-h-[96px]"
              placeholder="Edit this journal narrative..."
            />
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Link
              to={`/conversations/${session.id}`}
              className="text-xs text-[var(--color-brand-primary)] underline"
            >
              Open full conversation
            </Link>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="text-xs text-[var(--color-text-secondary)] inline-flex items-center gap-1"
              >
                <Pencil size={12} />
                Edit narrative
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={() => {
                    setDraft(narrative);
                    setEditing(false);
                  }}
                  className="text-xs text-[var(--color-text-secondary)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNarrative}
                  disabled={saving}
                  className="text-xs text-[var(--color-brand-primary)]"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
            {feedback && (
              <span className="text-[11px] text-[var(--color-text-muted)]">{feedback}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function getSessionDate(session: Session): Date {
  if (session.startedAt?.toDate) return session.startedAt.toDate();
  return new Date((session.startedAt as unknown as { seconds: number }).seconds * 1000);
}

function resolveMoodEnergyValue(...candidates: Array<number | undefined>): number | undefined {
  for (const candidate of candidates) {
    if (typeof candidate !== 'number' || !Number.isFinite(candidate) || candidate <= 0) continue;
    return Math.min(10, Math.max(1, Math.round(candidate)));
  }
  return undefined;
}

function firstNonEmpty(...candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
}

function sanitizeTextList(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return values
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter(Boolean);
}

